import React, { FunctionComponent, useState } from "react";
import { MDBCol, MDBRow } from "mdbreact";
import { FlexPackage } from "../../domain/entity/FlexPackage";
import { flexBenefitsPlanText } from "../../core/constants/strings";
import FlexBenefitsProductDropdown from "./FlexBenefitsProductDropdown";
import { dedupePackagesByIdentity } from "./FlexBenefitsPlanCard";

interface FlexBenefitsPlanGroupProps {
  packages: FlexPackage[];
}

/**
 * Sidebar plan-list row for FlexBenefits (PPI-1391 QA fix). A member can hold
 * several packages (PPI-1740); rendering one FlexBenefitsProductDropdown per
 * package directly in the plan list let that count leak into the UI as N
 * identical "FlexBenefits Plan" rows, breaking the single-row shape every
 * sibling (Dental/Vision/Latitude) uses.
 *
 * `packages` is the raw enrollment list, not one row per distinct package --
 * deduped first via dedupePackagesByIdentity so repeat enrollments of the same
 * package (confirmed live: one QA account has 25 enrollments across only 2
 * distinct packages) collapse to one entry instead of flooding the expanded
 * list with copies that would fetch the identical product breakdown. With
 * exactly one distinct package there's nothing to group, so its row renders on
 * its own; two or more nest behind one outer row. Either way each package row is
 * named by its own package name (PPI-1391 QA: the lone row read "FlexBenefits
 * Plan" and never named the plan the member actually holds), falling back to the
 * generic label only for a package the backend sent with no name.
 */
const FlexBenefitsPlanGroup: FunctionComponent<FlexBenefitsPlanGroupProps> = ({
  packages,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const distinctPackages = dedupePackagesByIdentity(packages);

  if (distinctPackages.length === 0) {
    return null;
  }

  if (distinctPackages.length === 1) {
    return (
      <FlexBenefitsProductDropdown
        pkg={distinctPackages[0]}
        index={0}
        label={distinctPackages[0].name ?? flexBenefitsPlanText}
      />
    );
  }

  return (
    <>
      <MDBRow className="mb-3">
        <MDBCol
          size="auto"
          className="d-flex align-items-center"
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: "pointer" }}
        >
          <p className="mb-0 mr-2" id="flexbenefits-plan-group-row">
            {flexBenefitsPlanText}
          </p>
          <i
            className={`fa fa-angle-${isOpen ? "up" : "down"} silver-text`}
            id="flexbenefits-plan-group-toggle"
          />
        </MDBCol>
      </MDBRow>
      {isOpen && (
        <div className="pl-3" id="flexbenefits-plan-group-packages">
          {distinctPackages.map((pkg, index) => (
            <FlexBenefitsProductDropdown
              key={pkg.id ?? index}
              pkg={pkg}
              index={index}
              label={pkg.name ?? flexBenefitsPlanText}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default FlexBenefitsPlanGroup;
